import React, { useEffect, useRef, useState, Suspense } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Navbar } from './Navbar';
import { Footer } from './Footer';
import { MobileTabBar, tabBarVisibleFor } from './MobileTabBar';
import { PageTransition } from '../motion';
import { IconSprite } from '../icons/SpriteIcons';
import { ConfirmDialogHost } from '../ui';

/**
 * Suspense fallback that only reveals the spinner after a short delay. Lazy
 * route chunks usually resolve in well under this window, so navigating to an
 * already-loaded (or quickly-loaded) route shows nothing instead of flashing a
 * spinner for a single frame - which reads as jank. The spinner only appears
 * for genuinely slow loads, where it's reassurance rather than a flash.
 */
const PageLoadingFallback: React.FC = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setShow(true), 180);
    return () => window.clearTimeout(t);
  }, []);
  return (
    <div className="flex min-h-[60vh] items-center justify-center" aria-hidden={!show}>
      {show ? <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/10 border-t-primary" /> : null}
    </div>
  );
};

export const MainLayout: React.FC = () => {
  const location = useLocation();
  const cursorGlowRef = useRef<HTMLDivElement>(null);
  // Pages that keep the tab bar need bottom room for it; pages that hide it
  // (event detail, checkout, auth, payment) manage their own bottom spacing.
  const hasTabBar = tabBarVisibleFor(location.pathname);
  // Auth owns the whole screen (its own logo, its own back-to-home link) and is
  // sized to exactly one viewport with no page scroll - a persistent navbar on
  // top of that would both duplicate the branding and push it past 100vh.
  const hideNavbar = location.pathname.startsWith('/auth');

  // The cursor glow is a pre-rendered radial gradient that we only *translate*
  // (compositor-only - no repaint, no blur filter). Position updates are written
  // straight to the DOM once per animation frame so tracking costs zero React
  // re-renders. Skipped entirely on touch devices and for reduced-motion users.
  useEffect(() => {
    const el = cursorGlowRef.current;
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!el || !canHover || reduce) return;

    let frame: number | null = null;
    let x = 0;
    let y = 0;
    const onMove = (e: MouseEvent) => {
      x = e.clientX;
      y = e.clientY;
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        el.style.transform = `translate3d(${x - 350}px, ${y - 350}px, 0)`;
        el.style.opacity = '1';
        frame = null;
      });
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div
      className={`min-h-screen flex flex-col bg-[#0b0b0f] text-white font-sans selection:bg-primary/30 selection:text-white relative z-0 ${
        hasTabBar ? 'pb-[calc(64px+env(safe-area-inset-bottom))] lg:pb-0' : ''
      }`}
    >
      {/* Static ambient background: gradients only (no blur filters, no blend modes, no per-frame repaints) */}
      <div className="fixed inset-0 z-[-1] bg-[#0b0b0f] overflow-hidden pointer-events-none" aria-hidden="true">
        <div
          className="absolute inset-0 bg-grid-pattern bg-[length:40px_40px] opacity-[0.18]"
          style={{ maskImage: 'radial-gradient(ellipse 80% 70% at 50% 0%, black 20%, transparent 75%)' }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(60vw 60vw at -5% -10%, rgba(229,9,20,0.10), transparent 65%), radial-gradient(55vw 55vw at 105% 110%, rgba(229,9,20,0.08), transparent 65%)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black" />
        <div
          ref={cursorGlowRef}
          className="absolute left-0 top-0 h-[700px] w-[700px] opacity-0 transition-opacity duration-700 will-change-transform"
          style={{ background: 'radial-gradient(closest-side, rgba(255,255,255,0.05), rgba(229,9,20,0.04) 45%, transparent 75%)' }}
        />
      </div>

      <IconSprite />
      {!hideNavbar && <Navbar />}
      {/* The fixed bottom tab bar's clearance is applied to the outer flex column
          (above) so it pushes the footer clear too, not just the page content. */}
      <main className="flex-1 relative z-10">
        <Suspense fallback={<PageLoadingFallback />}>
          {/* Keyed on pathname: React remounts on navigation, re-running the
              enter fade. No AnimatePresence/exit, so the new page shows at once. */}
          <PageTransition key={location.pathname}>
            <Outlet />
          </PageTransition>
        </Suspense>
      </main>
      <Footer />
      <MobileTabBar />
      <ConfirmDialogHost />
    </div>
  );
};
